// src/features/clients/components/EditClientModal.jsx
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import toast from "react-hot-toast";

// ✅ Função auxiliar híbrida CPF/CNPJ
const formatCpfCnpj = (value) => {
  if (!value) return "";

  const numericValue = value.replace(/\D/g, ""); // Remove não-dígitos

  // Máscara de CPF (até 11 dígitos)
  if (numericValue.length <= 11) {
    return numericValue
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})/, "$1-$2")
      .replace(/(-\d{2})\d+?$/, "$1");
  }

  // Máscara de CNPJ (acima de 11 dígitos)
  return numericValue
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .replace(/(-\d{2})\d+?$/, "$1");
};

function EditClientModal({ isOpen, onClose, onSave, clientData }) {
  const {
    register,
    handleSubmit,
    reset,
  } = useForm();

  useEffect(() => {
    if (clientData) {
      // ✅ Aplica a máscara correta ao carregar, dependendo do tamanho do dado salvo
      reset({
        name: clientData.name,
        phone: clientData.phone,
        cpf: formatCpfCnpj(clientData.cpf || ""),
        city: clientData.city || "",
      });
    }
  }, [clientData, reset]);

  const onSubmit = async (data) => {
    if (!data.name) {
      toast.error("Nome é obrigatório.");
      return;
    }

    // ✅ Limpa a formatação (remove pontos, traços e barras) antes de salvar
    const cleanDoc = data.cpf ? data.cpf.replace(/\D/g, "") : "";

    // Validação simples de tamanho ao salvar (opcional, mas recomendada)
    if (
      cleanDoc.length > 0 &&
      cleanDoc.length !== 11 &&
      cleanDoc.length !== 14
    ) {
      toast.error(
        "Documento inválido (deve ter 11 dígitos para CPF ou 14 para CNPJ)."
      );
      return;
    }

    await onSave({
      ...data,
      cpf: cleanDoc,
      phone: clientData.phone,
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Cliente</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Nome
              </Label>
              <Input
                id="name"
                {...register("name", { required: "Nome é obrigatório" })}
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone" className="text-right">
                Telefone
              </Label>
              <Input
                id="phone"
                {...register("phone")}
                className="col-span-3 bg-gray-100 text-gray-500 cursor-not-allowed"
                disabled
                title="Para mudar o telefone, exclua e crie novamente."
              />
            </div>

            {/* ✅ 3. Campo Híbrido CPF/CNPJ */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="cpf" className="text-right">
                CPF/CNPJ
              </Label>
              <Input
                id="cpf"
                {...register("cpf", {
                  onChange: (e) => {
                    // Aplica a máscara dinâmica (muda de visual ao passar do 11º dígito)
                    e.target.value = formatCpfCnpj(e.target.value);
                  },
                })}
                className="col-span-3"
                placeholder="CPF ou CNPJ"
                maxLength={18} // Aumentado para caber CNPJ formatado (14 números + 4 símbolos)
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="city" className="text-right">
                Cidade
              </Label>
              <Input id="city" {...register("city")} className="col-span-3" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit">Salvar Alterações</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default EditClientModal;
